import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../../../utils/exportColors';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { jsPDF } from 'jspdf';
import {
    ensureKalpurushFontLoaded,
    FAMILY_CARD_FONT_FAMILY,
} from '../family-card-form/familyCardFont';
import { BASELINE_RATIO, LEADER, LINE_BOX } from './voterMigrationMetrics';

const EXPORT_SCALE = 2;

/**
 * Even with the line box pinned, html2canvas lands every baseline ~2.5 pt lower
 * than the browser — the same amount at every size, measured against the
 * preview. Lift each line by it so the export sits where CSS says.
 */
const EXPORT_BASELINE_DRIFT_PT = 2.5;

function prepareVoterMigrationClone(doc) {
    doc.querySelectorAll('[data-voter-migration-page]').forEach((root) => {
        root.style.overflow = 'visible';
        root.style.transform = 'none';
        root.style.fontFamily = FAMILY_CARD_FONT_FAMILY;
        // html2canvas can't parse oklch/lab/color() — rewrite any to rgb first.
        sanitizeExportColors(root);
        // html2canvas puts a line's baseline at the bottom of its line box
        // rather than where CSS does. Shrinking the line box to the baseline
        // offset lands the text on the same baselines as the preview, and an
        // explicit height keeps the room below it that the dots and descenders
        // need before the overflow clip bites.
        root.querySelectorAll('span').forEach((el) => {
            el.style.verticalAlign = 'baseline';
            if (Number.isNaN(parseFloat(el.style.top))) return;
            const size = parseFloat(el.style.fontSize) || 0;
            if (!size) return;
            el.style.lineHeight = `${BASELINE_RATIO * size}px`;
            el.style.height = `${LINE_BOX * size}px`;
            el.style.top = `${parseFloat(el.style.top) - EXPORT_BASELINE_DRIFT_PT}px`;
        });
        root.querySelectorAll('[data-field-value]').forEach((el) => {
            el.style.overflow = 'hidden';
            el.style.textOverflow = 'ellipsis';
            // Latin descenders drop further than the form's own text; give the
            // clip box extra room so g, j, p, q and y survive the export.
            const size = parseFloat(el.style.fontSize) || 0;
            if (size) el.style.height = `${size * 2}px`;
        });
        // html2canvas re-measures text, so the leaders keep their printed pitch.
        root.querySelectorAll('[data-leader]').forEach((el) => {
            el.style.letterSpacing = `${LEADER.letterSpacing}px`;
            el.style.overflow = 'hidden';
            el.style.whiteSpace = 'nowrap';
        });
    });
}

/**
 * Clone off-screen at 1:1 scale — html2canvas clips text inside CSS transform: scale().
 * @param {HTMLElement} element
 */
function cloneForExport(element) {
    const clone = element.cloneNode(true);
    const width = element.scrollWidth;

    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = [
        'position:fixed',
        'left:-20000px',
        'top:0',
        `width:${width}px`,
        'overflow:visible',
        'pointer-events:none',
        'z-index:-1',
    ].join(';');

    clone.style.transform = 'none';
    clone.style.transformOrigin = 'top left';
    clone.style.overflow = 'visible';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    return { clone, wrapper, width };
}

/**
 * Each `[data-voter-migration-page]` becomes one A4 page, so the export keeps
 * the two-page shape of the printed form.
 * @param {HTMLElement} element The node wrapping both pages.
 * @returns {Promise<Blob>}
 */
export async function generateVoterMigrationPdfBlob(element) {
    await ensureKalpurushFontLoaded();
    const { clone, wrapper } = cloneForExport(element);

    try {
        await document.fonts.ready;
        const pages = Array.from(clone.querySelectorAll('[data-voter-migration-page]'));
        const targets = pages.length ? pages : [clone];

        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        for (let i = 0; i < targets.length; i += 1) {
            const target = targets[i];
            const width = target.scrollWidth;
            const height = target.scrollHeight;

            // eslint-disable-next-line no-await-in-loop
            const canvas = await html2canvas(target, {
                scale: EXPORT_SCALE,
                width,
                height,
                windowWidth: width,
                windowHeight: height,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: '#ffffff',
                onclone: prepareVoterMigrationClone,
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;
            let drawWidth = pdfWidth;
            let drawHeight = imgHeight;

            if (imgHeight > pdfHeight) {
                const fit = pdfHeight / imgHeight;
                drawWidth = pdfWidth * fit;
                drawHeight = pdfHeight;
            }

            if (i > 0) pdf.addPage();
            const x = (pdfWidth - drawWidth) / 2;
            pdf.addImage(imgData, 'JPEG', x, 0, drawWidth, drawHeight, undefined, 'FAST');
        }

        return pdf.output('blob');
    } finally {
        wrapper.remove();
    }
}

export async function downloadVoterMigrationPdf(element, filename = 'voter-migration-form-13.pdf') {
    const blob = await generateVoterMigrationPdfBlob(element);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/**
 * Print the generated PDF from the current page. `printBlob` renders the pages
 * into a hidden same-origin iframe and prints that, so the dialog opens right
 * here instead of in a second tab (a pop-up tab is only the fallback if
 * rendering fails).
 * @param {HTMLElement} element
 * @param {string} popupBlockedMessage
 */
export async function printVoterMigrationForm(element, popupBlockedMessage) {
    const blob = await generateVoterMigrationPdfBlob(element);
    const opened = await printBlob(blob);
    if (!opened) throw new Error(popupBlockedMessage);
}
